import {
  Etat
} from '../services/abstracts/ServiceAbstract'

export default class ServiceManager {
  constructor(parameters, CookieManager) {
    this.parameters = parameters
    this.CookieManager = CookieManager
    this.services = []
    this.servicesToLoad = []
    this.managers = []
    this.loadingManagers = []
  }

  // Add service
  addService(settings) {
    if (!settings.key || !settings.identifier) {
      throw new Error('The settings "key" and "identifier" are required')
    }

    // mark this service as loading
    this.servicesToLoad.push({
      key: settings.key,
      manager: settings.manager || null
    })

    // dynamically load and instantiate service class
    this.loadServiceClass(settings.key)
      .then(async ({
        default: serviceClass
      }) => {
        const service = new serviceClass(settings, this.CookieManager)

        // this service has a manager: try to load it
        if (settings.manager) {
          // sanitize manager name and check if it's not already loading
          const managerName = settings.manager.replace(/[^a-zA-Z_\/]/g, '')
          if (!this.managerIsLoading(managerName)) {
            await this.addManager(managerName)
          }

          const manager = await this.getManager(managerName)
          manager.addManagedService(service)
        }

        // add this service to services list
        this.registerService(service)
      })
  }

  registerService(service) {
    this.services.push(service)

    // dispatch registered event
    service.sendCustomEvent(`rsq.service.${service.key}.registered`)
  }

  async addManager(managerName) {
    // mark this manager as loading (to prevent multiple loads)
    this.loadingManagers.push(managerName)

    // dynamically load and instantiate manager class
    await this.loadManagerClass(managerName)
      .then(({
        default: managerClass
      }) => {
        // add this manager to managers list
        const manager = new managerClass(this)
        this.registerManager(manager)
      })
      .catch((error) => {
        throw new Error(error)
      })
  }

  registerManager(manager) {
    // remove this manager from loading array
    const index = this.loadingManagers.indexOf(manager.getSimpleName())
    this.loadingManagers.splice(index, 1)

    // add this manager to managers list
    this.managers.push(manager)
  }

  async getManager(managerName) {
    return new Promise((resolve, reject) => {
      if (this.managerIsLoading(managerName)) {
        document.addEventListener(`rsq.manager.${managerName}.loaded`, (e) => {
          resolve(e.detail.emitter)
        }, {
          once: true
        })
        return
      }

      for (const manager of this.managers) {
        if (manager.getSimpleName() === managerName) {
          resolve(manager)
          return
        }
      }

      reject(`Manager ${managerName} not found.`)
    })
  }

  managerIsLoading(managerName) {
    return this.loadingManagers.indexOf(managerName) !== -1
  }

  // initialize services status
  async initServices() {
    const initializingServices = []
    for (const service of this.services) {
      // do additional operations at service init if necessary
      initializingServices.push(service.callInit())
    }

    // wait for all services to be initialized before we set their status
    await Promise.all(initializingServices)

    for (const service of this.services) {
      // set service status (it will call wait() / write() or fallback() method)
      await service.setStatus(this.CookieManager.getServiceStatus(service) ? this.CookieManager.getServiceStatus(service) : Etat.wait)

      // check if service can be enabled without consent
      service.checkConsentNeeded()
    }
  }

  allServicesLoaded() {
    return new Promise((resolve) => {
      const hasServicesToLoad = () => this.servicesToLoad.length > this.services.length

      // initial check
      if (!hasServicesToLoad()) {
        resolve(true)
      } else {
        // wait for all services to be registered
        for (const service of this.servicesToLoad) {
          document.addEventListener(`rsq.service.${service.key}.registered`, () => {
            if (!hasServicesToLoad()) {
              resolve(true)
            }
          }, {
            once: true
          })
        }
      }
    })
  }

  getService(key) {
    for (const service of this.services) {
      if (service.key === key) {
        return service
      }
    }
    return false
  }

  getServices() {
    return this.services
  }

  loadManagerClass(managerName) {
    return import(
      /* webpackChunkName: "manager-[request]" */
      /* webpackPreload: true */
      `../services/managers/${this.toKebab(managerName)}`)
  }

  loadServiceClass(serviceName) {
    const serviceFilename = this.toKebab(serviceName)
    switch (this.getServiceType(serviceName)) {
      case 'embedded':
        return import(
          /* webpackChunkName: "service-[request]" */
          /* webpackPreload: true */
          `../services/embedded/${serviceFilename}`)
      default:
        return import(
          /* webpackChunkName: "service-[request]" */
          /* webpackPreload: true */
          `../services/generic/${serviceFilename}`)
    }
  }

  getServiceType(serviceName) {
    switch (serviceName) {
      case 'Dailymotion':
      case 'FacebookCustomerChat':
      case 'Facebook':
      case 'Vimeo':
      case 'YouTube':
        return 'embedded'
      default:
        return 'regular'
    }
  }

  toKebab(name) {
    return name
      .replace(/([A-Z])([A-Z])/g, '$1-$2')
      .replace(/([a-z])([A-Z])/g, '$1-$2')
      .replace(/[\s_]+/g, '-')
      .toLowerCase()
  }
}
